import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const SIDE_IMG = "https://images.unsplash.com/photo-1751374858042-b8b9ff8480aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwYnVzaW5lc3MlMjBwZW9wbGV8ZW58MHx8fHwxNzg5OTczNjMxfDA&ixlib=rb-4.1.0&q=85";

function AuthShell({ children, title, sub }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <img src="/africashop.png" alt="" className="w-10 h-10 rounded-xl object-contain" />
            <span className="font-display font-extrabold text-xl">AfriMarket</span>
          </Link>
          <div>
            <h2 className="text-3xl font-display font-extrabold leading-tight">La marketplace multi-boutiques de l'Afrique</h2>
            <p className="text-neutral-300 mt-3 max-w-sm">Vendez, achetez, gagnez des bonus. Multi-devises et Mobile Money.</p>
          </div>
        </div>
      </div>
      <div className="relative h-52 lg:hidden">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/50" />
        <Link to="/" className="relative flex h-full flex-col items-center justify-center gap-2 text-white">
          <img src="/africashop.png" alt="" className="h-16 w-16 object-contain" />
          <span className="font-display text-xl font-extrabold">AfriMarket</span>
        </Link>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-display font-extrabold">{title}</h1>
          {sub && <p className="text-muted-foreground text-sm mt-1 mb-6">{sub}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

function redirectFor(user, from) {
  const staff = ["SUPER_ADMIN", "ADMIN", "SHOP_ADMIN", "PRODUCT_MANAGER", "ORDER_MANAGER", "MODERATOR", "ACCOUNTANT"];
  if (staff.includes(user.role)) return "/admin";
  if (user.role === "MERCHANT") return "/merchant";
  if (user.role === "RELAY_MANAGER" || user.role === "CARRIER_AGENT") return "/partner";
  const blocked = ["/login", "/register", "/forgot-password", "/reset-password"];
  const ok = from && from.startsWith("/") && !from.startsWith("//") && !blocked.some((p) => from.startsWith(p))
    && !from.startsWith("/admin") && !from.startsWith("/merchant");
  return ok ? from : "/";
}

export function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null); // {challenge_id, channel, dev_code}
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.require_2fa) {
        setChallenge(data);
        if (data.dev_code) toast.info(`Code de vérification (démo) : ${data.dev_code}`);
      } else {
        login(data.token, data.user);
        navigate(redirectFor(data.user, from), { replace: true });
      }
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id: challenge.challenge_id, code });
      login(data.token, data.user);
      navigate(redirectFor(data.user, from), { replace: true });
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const channelLabel = { email: "e-mail", sms: "SMS", whatsapp: "WhatsApp" };

  if (challenge) {
    return (
      <AuthShell title="Vérification en deux étapes" sub={`Saisissez le code envoyé par ${channelLabel[challenge.channel] || challenge.channel}`}>
        <form onSubmit={verify2fa} className="space-y-4">
          <div>
            <Label htmlFor="otp">Code de vérification</Label>
            <Input id="otp" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required data-testid="twofa-code" className="mt-1 tracking-[0.4em] text-center text-lg" placeholder="000000" />
          </div>
          {error && <div className="text-sm text-destructive" data-testid="twofa-error">{error}</div>}
          <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="twofa-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Vérifier
          </Button>
          <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="text-sm text-muted-foreground hover:text-primary w-full text-center" data-testid="twofa-back">
            Retour à la connexion
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("login")} sub="Accédez à votre compte AfriMarket">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">{t("email")}</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="password">{t("password")}</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1" />
        </div>
        {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
        <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("login")}
        </Button>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-primary" data-testid="forgot-link">{t("forgot_password")}</Link>
          <Link to="/register" className="text-muted-foreground hover:text-primary">{t("register")}</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { t, lang } = useI18n();
  const { login, user, switchProfile } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", confirm: "",
    role: sp.get("role") === "merchant" ? "MERCHANT" : "CLIENT",
    country: "", invite_code: "",
  });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMerchantTerms, setAcceptedMerchantTerms] = useState(false);
  const [activatingMerchant, setActivatingMerchant] = useState(false);
  const [pendingMsg, setPendingMsg] = useState("");
  const isMerchantProfileFlow = !!user && form.role === "MERCHANT";

  useEffect(() => {
    api.get("/config/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedCountry = countries.find((c) => c.name === form.country);
  const selectedCurrency = selectedCountry?.currency;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.country) { setError("Veuillez sélectionner votre pays."); return; }
    if (!acceptedTerms) { setError("Veuillez accepter les conditions d’utilisation."); return; }
    if (form.role === "MERCHANT" && !acceptedMerchantTerms) {
      setError("Veuillez accepter les conditions commerçant."); return;
    }
    if (form.password !== form.confirm) { setError("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name, email: form.email, phone: form.phone ? `${selectedCountry.phone_code}${form.phone}` : "", password: form.password,
        role: form.role, country: form.country, invite_code: form.role === "MERCHANT" ? null : form.invite_code || null,
        merchant_terms_accepted: form.role === "MERCHANT" && acceptedMerchantTerms, language: lang,
      });
      if (data.pending) { setPendingMsg(data.message); return; }
      login(data.token, data.user);
      toast.success("Compte créé !");
      if (data.restore_requested) toast.info(data.message, { duration: 10000 });
      navigate(form.role === "MERCHANT" ? "/merchant/onboarding" : "/account");
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const addMerchantProfile = async () => {
    setError("");
    if (!acceptedMerchantTerms) {
      setError("Veuillez accepter les conditions commerçant.");
      return;
    }
    setActivatingMerchant(true);
    try {
      const { data } = await api.post("/auth/merchant-profile", { use_account_info: true, accept_terms: true });
      login(data.token, data.user);
      toast.success("Profil commerçant activé.");
      navigate("/merchant/onboarding?new=1");
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setActivatingMerchant(false);
    }
  };

  if (pendingMsg) {
    return (
      <AuthShell title="Inscription envoyée" sub="Compte partenaire logistique">
        <div className="text-sm bg-green-50 border border-green-200 rounded-lg p-4" data-testid="reg-pending">{pendingMsg}</div>
        <Link to="/login" className="block text-center text-primary text-sm mt-4">Retour à la connexion</Link>
      </AuthShell>
    );
  }

  if (isMerchantProfileFlow) {
    const hasMerchantProfile = user.role === "MERCHANT" || (user.profiles || []).includes("MERCHANT");
    return (
      <AuthShell title="Créer votre profil commerçant" sub="Votre profil client et commerçant partageront le même compte">
        {hasMerchantProfile ? (
          <div className="space-y-4">
            <p className="text-sm">Un profil commerçant existe déjà pour ce compte.</p>
            <Button className="w-full rounded-full h-11" onClick={async () => {
              try {
                await switchProfile("MERCHANT");
                navigate("/merchant");
              } catch {
                setError("Impossible de changer de profil. Veuillez réessayer.");
              }
            }}>Accéder à mon espace commerçant</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">Souhaitez-vous utiliser les informations de votre compte client pour créer le profil commerçant ?</p>
            <dl className="rounded-lg border border-border bg-muted/40 p-3 text-sm space-y-2">
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Nom</dt><dd className="text-right">{user.name}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Pays</dt><dd className="text-right">{user.country || "Non renseigné"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Téléphone</dt><dd className="text-right">{user.phone || "Non renseigné"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">E-mail</dt><dd className="text-right">{user.email}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Mot de passe</dt><dd className="text-right">Identifiants actuels conservés</dd></div>
            </dl>
            <label className="flex items-start gap-2 text-sm leading-relaxed">
              <input type="checkbox" checked={acceptedMerchantTerms} onChange={(e) => setAcceptedMerchantTerms(e.target.checked)}
                required data-testid="merchant-terms-accept" className="mt-1 accent-primary" />
              <span>
                J’accepte les <Link to="/terms/merchant" target="_blank" rel="noreferrer" className="text-primary underline">conditions d’utilisation relatives aux commerçants</Link>.
              </span>
            </label>
            {error && <div className="text-sm text-destructive" data-testid="reg-error">{error}</div>}
            <Button className="w-full rounded-full h-11" disabled={activatingMerchant || !acceptedMerchantTerms}
              onClick={addMerchantProfile} data-testid="merchant-profile-confirm">
              {activatingMerchant && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Oui, utiliser ces informations
            </Button>
            <Button variant="outline" className="w-full rounded-full h-11" disabled={activatingMerchant}
              onClick={() => navigate("/account")} data-testid="merchant-profile-cancel">
              Annuler la création
            </Button>
          </div>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("register")} sub="Rejoignez la plateforme en quelques secondes">
      <div className="grid grid-cols-2 gap-1 mb-5 bg-muted p-1 rounded-2xl">
        {[["CLIENT", "Client"], ["MERCHANT", "Commerçant"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => upd("role", v)} data-testid={`role-${v}`}
            className={`py-2 rounded-full text-sm font-medium transition-colors ${form.role === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            {l}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>{t("name")}</Label>
          <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="reg-name" className="mt-1" />
        </div>
        <div>
          <Label>{t("country")}</Label>
          <Select value={form.country} onValueChange={(v) => upd("country", v)}>
            <SelectTrigger className="mt-1" data-testid="reg-country"><SelectValue placeholder="Sélectionner un pays" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {countries.map((c) => (
                <SelectItem key={c.name} value={c.name}>{c.name} — {c.currency}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCurrency && <div className="text-xs text-muted-foreground mt-1">Monnaie : {selectedCurrency}</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("email")}</Label>
            <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="reg-email" className="mt-1" />
          </div>
          <div>
            <Label>{t("phone")}</Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground" data-testid="reg-phone-code">
                {selectedCountry?.phone_code || "+"}
              </span>
              <Input value={form.phone} onChange={(e) => upd("phone", e.target.value.replace(/\D/g, ""))}
                inputMode="tel" placeholder={selectedCountry ? "Numéro de téléphone" : "Choisissez un pays"}
                disabled={!selectedCountry} data-testid="reg-phone" className="rounded-l-none" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("password")}</Label>
            <Input type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} required data-testid="reg-password" className="mt-1" />
          </div>
          <div>
            <Label>Confirmer</Label>
            <Input type="password" value={form.confirm} onChange={(e) => upd("confirm", e.target.value)} required data-testid="reg-confirm" className="mt-1" />
          </div>
        </div>
        {form.role !== "MERCHANT" && <div>
          <Label>Code d'invitation (optionnel)</Label>
          <Input value={form.invite_code} onChange={(e) => upd("invite_code", e.target.value)} placeholder="INVITE-XXXXXX" data-testid="reg-invite" className="mt-1" />
        </div>}
        {form.role === "MERCHANT" && (
          <label className="flex items-start gap-2 text-sm leading-relaxed" data-testid="reg-merchant-terms-label">
            <input type="checkbox" checked={acceptedMerchantTerms} onChange={(e) => setAcceptedMerchantTerms(e.target.checked)}
              required data-testid="reg-merchant-terms" className="mt-1 accent-primary" />
            <span>
              J’accepte les <Link to="/terms/merchant" target="_blank" rel="noreferrer" className="text-primary underline">conditions d’utilisation relatives aux commerçants</Link>.
            </span>
          </label>
        )}
        {error && <div className="text-sm text-destructive" data-testid="reg-error">{error}</div>}
        <label className="flex items-start gap-2 text-sm leading-relaxed" data-testid="reg-terms-label">
          <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}
            required data-testid="reg-terms" className="mt-1 accent-primary" />
          <span>
            J’accepte les <Link to="/terms" target="_blank" rel="noreferrer" className="text-primary underline">Conditions générales d’utilisation et de vente</Link> d’AfriMarket.
          </span>
        </label>
        <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="reg-submit">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("register")}
        </Button>
        <div className="text-sm text-center text-muted-foreground">
          Déjà un compte ? <Link to="/login" className="text-primary">{t("login")}</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function CompletePartnerInvitation() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [invitation, setInvitation] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("Lien d’invitation invalide ou incomplet.");
      setLoading(false);
      return;
    }
    api.get(`/logistics/invitations/${encodeURIComponent(token)}`)
      .then((r) => setInvitation(r.data.invitation))
      .catch((e) => setError(apiErr(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!acceptTerms) {
      setError("Veuillez accepter les conditions d’utilisation.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/logistics/invitations/complete", {
        token, name, phone, password, accept_terms: acceptTerms,
      });
      login(data.token, data.account);
      toast.success("Votre compte partenaire est créé.");
      navigate("/partner", { replace: true });
    } catch (e) { setError(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Terminer la création de votre compte" sub="Invitation AfriMarket — espace logistique">
      {loading ? <div className="text-sm text-muted-foreground">Vérification du lien…</div> : invitation ? (
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
            <div>Adresse e-mail : <strong>{invitation.email}</strong></div>
            <div>Compte : <strong>{invitation.role === "RELAY_MANAGER" ? "Point relais" : "Transporteur"}</strong></div>
            <div>Pays : <strong>{invitation.country}</strong></div>
          </div>
          <div><Label>Nom complet</Label><Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={120} className="mt-1" data-testid="partner-invite-name" /></div>
          <div><Label>Téléphone (facultatif)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" className="mt-1" data-testid="partner-invite-phone" /></div>
          <div><Label>Mot de passe (8 caractères minimum)</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" className="mt-1" data-testid="partner-invite-password" /></div>
          <div><Label>Confirmer le mot de passe</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" className="mt-1" data-testid="partner-invite-confirm" /></div>
          <label className="flex items-start gap-2 text-sm leading-relaxed">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required className="mt-1 accent-primary" data-testid="partner-invite-terms" />
            <span>J’accepte les <Link to="/terms" target="_blank" rel="noreferrer" className="text-primary underline">conditions générales d’utilisation</Link>.</span>
          </label>
          {error && <div className="text-sm text-destructive" role="alert">{error}</div>}
          <Button type="submit" className="w-full rounded-full h-11" disabled={busy || !acceptTerms} data-testid="partner-invite-complete">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer mon compte
          </Button>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-destructive" role="alert">{error || "Ce lien d’invitation n’est plus valide."}</div>
          <Link to="/login" className="block text-center text-primary text-sm">Retour à la connexion</Link>
        </div>
      )}
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setDone(true); setLoading(false);
  };
  return (
    <AuthShell title="Mot de passe oublié" sub="Recevez un lien de réinitialisation">
      {done ? (
        <div className="text-sm text-muted-foreground">
          Si cet e-mail est enregistré, un lien de réinitialisation a été envoyé.
          <div className="mt-4"><Link to="/login" className="text-primary">Retour à la connexion</Link></div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" className="mt-1" />
          </div>
          <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="forgot-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Envoyer le lien
          </Button>
          <div className="text-sm text-center"><Link to="/login" className="text-primary">Retour à la connexion</Link></div>
        </form>
      )}
    </AuthShell>
  );
}

export function ConfirmDeletion() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const { logout } = useAuth();
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const confirm = async () => {
    setLoading(true); setError("");
    try { const { data } = await api.post("/account/deletion/confirm", { token }); logout(); setDone(data.message); }
    catch (err) { setError(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Suppression définitive du compte" sub="Cette action efface vos données personnelles">
      {done ? (
        <div className="space-y-4" data-testid="deletion-done">
          <p className="text-sm">{done}</p>
          <Link to="/" className="text-primary text-sm hover:underline">Retour à l'accueil</Link>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Votre nom, e-mail et téléphone seront effacés. Vos commandes et factures sont conservées de façon anonyme pour la comptabilité, puis tout est effacé après 3 mois.</p>
          {error && <div className="text-sm text-destructive" data-testid="deletion-confirm-error">{error}</div>}
          <Button variant="destructive" className="w-full rounded-full h-11" disabled={loading || !token} onClick={confirm} data-testid="deletion-confirm-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Confirmer la suppression définitive
          </Button>
          <Link to="/" className="block text-center text-sm text-muted-foreground hover:text-primary">Annuler</Link>
        </div>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe réinitialisé");
      navigate("/login");
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthShell title="Réinitialiser le mot de passe" sub="Choisissez un nouveau mot de passe">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Nouveau mot de passe</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password" className="mt-1" />
        </div>
        {error && <div className="text-sm text-destructive">{error}</div>}
        <Button type="submit" className="w-full rounded-full h-11" disabled={loading || !token} data-testid="reset-submit">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Réinitialiser
        </Button>
        <div className="text-sm text-center"><Link to="/login" className="text-primary">Retour à la connexion</Link></div>
      </form>
    </AuthShell>
  );
}
